import { useEffect, useRef, useCallback } from "react";

export interface UseHoverTooltipOptions {
  /**
   * Delay in milliseconds before closing the tooltip after mouse leave
   * @default 100
   */
  closeDelay?: number;
}

/**
 * Custom hook to manage hover interactions for tooltips.
 * Handles the complex state management for opening/closing tooltips on hover
 * with support for hovering over both the trigger and the tooltip content.
 *
 * @param options - Configuration options for the tooltip behavior
 * @returns Object containing refs and event handlers for tooltip interaction
 */
export const useHoverTooltip = (options: UseHoverTooltipOptions = {}) => {
  const { closeDelay = 100 } = options;

  const tooltipOpenRef = useRef(false);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hoverTriggerRef = useRef(false);
  const hoverTooltipRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (!closeTimeoutRef.current) return;
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }, []);

  const getTrigger = useCallback(() => {
    if (!triggerRef.current && wrapperRef.current) {
      triggerRef.current = wrapperRef.current.querySelector(
        "button",
      ) as HTMLButtonElement | null;
    }
    return triggerRef.current;
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!tooltipOpenRef.current) return;
      if (hoverTriggerRef.current || hoverTooltipRef.current) return;
      getTrigger()?.click();
    }, closeDelay);
  }, [clearCloseTimeout, getTrigger, closeDelay]);

  const handleTriggerEnter = useCallback(() => {
    hoverTriggerRef.current = true;
    clearCloseTimeout();
    if (tooltipOpenRef.current) return;
    getTrigger()?.click();
  }, [clearCloseTimeout, getTrigger]);

  const handleTriggerLeave = useCallback(() => {
    hoverTriggerRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const handleTooltipEnter = useCallback(() => {
    hoverTooltipRef.current = true;
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleTooltipLeave = useCallback(() => {
    hoverTooltipRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const handleTooltipOpen = useCallback(() => {
    tooltipOpenRef.current = true;
  }, []);

  const handleTooltipClose = useCallback(() => {
    tooltipOpenRef.current = false;
    hoverTooltipRef.current = false;
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  return {
    tooltipOpenRef,
    wrapperRef,
    handleTriggerEnter,
    handleTriggerLeave,
    handleTooltipEnter,
    handleTooltipLeave,
    handleTooltipOpen,
    handleTooltipClose,
  };
};
